import { BlueLinkSecondaryUnderlined, LabelStrong } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { CreditCard } from '@liveauctioneers/caterwaul-components/lib/types/CreditCard';
import {
    creditCardsAnyLoadingSelector,
    fetchLAPaymentCardIfNeeded,
    getLAPaymentCardsLoaded,
    laPaymentCardsForProviderSelector,
} from '@/redux/modules/creditCard';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogPaymentProvider } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { openAddCreditCardModal } from '@/redux/modules/modal';
import { PaymentMethods } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import CardOnFile from '@liveauctioneers/caterwaul-components/lib/CardOnFile/CardOnFile';
import CardSelection from '@/components/PaymentMethodSelection/CardSelection';
import PaymentMethod from './PaymentMethod';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';

type Props = {
    active: boolean;
    catalogId: number;
    onCardSelectionChange: (creditCard: CreditCard) => void;
    onPaymentMethodChange: (paymentMethod: PaymentMethods) => void;
    paymentMethod: PaymentMethods;
    selectedCreditCard: CreditCard;
};

const PaymentMethodCards = ({
    active,
    catalogId,
    onCardSelectionChange,
    onPaymentMethodChange,
    paymentMethod,
    selectedCreditCard,
}: Props) => {
    const dispatch = useAppDispatch();
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));

    const laPaymentCardLoaded = useAppSelector(getLAPaymentCardsLoaded);
    const cardsLoading = useAppSelector(creditCardsAnyLoadingSelector);
    const creditCards = useAppSelector((state) => laPaymentCardsForProviderSelector(state, paymentProvider));

    useEffect(() => {
        if (!Boolean(laPaymentCardLoaded)) {
            dispatch(fetchLAPaymentCardIfNeeded());
        }
    }, [dispatch, laPaymentCardLoaded]);

    const selected = paymentMethod === PaymentMethods.CreditCard;

    if (!active) {
        if (selected) {
            return (
                selectedCreditCard && (
                    <CardOnFile
                        creditCard={selectedCreditCard}
                        isDefault={false}
                    />
                )
            );
        } else {
            return null;
        }
    }

    return (
        <PaymentMethod
            data-testid="payment-method-card"
            onClick={() => onPaymentMethodChange(PaymentMethods.CreditCard)}
            selected={selected}
            title={
                <LabelStrong>
                    <FormattedMessage id="checkout.payment.creditCards" />
                </LabelStrong>
            }
        >
            {cardsLoading ? (
                <Throbber testid="payment-method-card-loading" />
            ) : (
                <>
                    <CardSelection
                        creditCards={creditCards}
                        selectCard={onCardSelectionChange}
                    />
                    <StyledAddNewPaymentMethod>
                        <BlueLinkSecondaryUnderlined
                            onClick={() =>
                                dispatch(
                                    openAddCreditCardModal(paymentProvider, {
                                        isCheckout: true,
                                    })
                                )
                            }
                        >
                            <FormattedMessage id="checkout.payment.addCreditCard" />
                        </BlueLinkSecondaryUnderlined>
                    </StyledAddNewPaymentMethod>
                </>
            )}
        </PaymentMethod>
    );
};

export default PaymentMethodCards;

const StyledAddNewPaymentMethod = styled.div`
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 32px;
    }
`;
